import CoreBase from '../../../services/engines/CoreBase';
import { toPng } from 'html-to-image';
import UmlPkgLibBuilder from './UmlPkgLibBuilder.js';
import umlCanvasEngine from '../../Core/UmlCanvasEngine.js';
import umlConnectorEngine from '../../Core/UmlConnectorEngine.js';
import { collectShapeIdsDeep } from '../../Core/UmlCanvasEngineHelper.js';

// prettier-ignore
import { CANVAS_WORLD_SELECTOR, waitForPaint, computeFitTransform, prepareCloneForCapture, } from '../../UI/UmlCanvas/UmlPreviewDlgHelpers.js';
import umlConst from '@/UmlWorkspace/Types/UmlConsts';

/**
 * Builds a package+satellites preview thumbnail (PNG dataUrl) for a UmlPkgLib
 */
class UmlPkgLibPreviewBuilder extends CoreBase {
    /**
     * @param {object} packageShape - the selected UmlPackageShape (live instance)
     * @param {HTMLElement} container - on-screen capture host, owned by UmlPkgSaveDlg (previewRef.current)
     * @returns {Promise<string|null>} PNG dataUrl, or null if inputs/DOM aren't ready
     */
    build = async (packageShape, container) => {
        const liveWorld = document.querySelector(CANVAS_WORLD_SELECTOR);
        if (!liveWorld || !container || !packageShape) return null;

        try {
            const {
                package: pkg,
                satellites,
                connectors,
            } = UmlPkgLibBuilder.build(packageShape, umlCanvasEngine.shapes, umlConnectorEngine.connectors);

            const bounds = UmlPkgLibBuilder.getPreviewBounds(pkg, satellites);
            const includedShapeIds = new Set([...collectShapeIdsDeep(packageShape), ...satellites.map((satellite) => satellite.id)]);
            const includedConnectorIds = new Set(connectors.map((connector) => connector.id));

            const clone = liveWorld.cloneNode(true);
            clone.querySelectorAll('[data-shape-id]').forEach((el) => {
                if (!includedShapeIds.has(el.dataset.shapeId)) el.remove();
            });
            clone.querySelectorAll('[data-connector-id]').forEach((el) => {
                if (!includedConnectorIds.has(el.dataset.connectorId)) el.remove();
            });

            const preview = umlConst.PKG_PREVIEW();
            clone.style.transform = computeFitTransform(bounds, preview.w, preview.h, preview.p);

            prepareCloneForCapture(clone);

            container.innerHTML = '';
            container.appendChild(clone);

            await waitForPaint();
            const dataUrl = await toPng(container, { skipFonts: true });
            container.innerHTML = '';
            return dataUrl;
        } catch (error) {
            this.logError('build', error.message);
            return null;
        }
    };
}

const umlPkgLibPreviewBuilder = new UmlPkgLibPreviewBuilder();
export default umlPkgLibPreviewBuilder;
